import type { Router } from 'vue-router';
import type { useSessionStore } from './stores/session.ts';
import type { useWorkspaceStore } from './stores/workspace.ts';

/**
 * Paused mid-use (the owner's plan): reload the account and its organizations,
 * then go to the same address again, so the router's guard sends it to the
 * paused page. One at a time, however many requests are refused.
 */
export class PausedRedirect {
  readonly #router: Router;
  readonly #session: ReturnType<typeof useSessionStore>;
  readonly #workspace: ReturnType<typeof useWorkspaceStore>;
  #running: Promise<void> | null = null;

  constructor(deps: {
    router: Router;
    session: ReturnType<typeof useSessionStore>;
    workspace: ReturnType<typeof useWorkspaceStore>;
  }) {
    this.#router = deps.router;
    this.#session = deps.session;
    this.#workspace = deps.workspace;
  }

  handle(): Promise<void> {
    if (!this.#session.isAuthenticated) return Promise.resolve();
    this.#running ??= this.#run().finally(() => {
      this.#running = null;
    });
    return this.#running;
  }

  async #run(): Promise<void> {
    await Promise.all([this.#workspace.loadOrgs(), this.#session.refresh()]);
    const { path, query, hash } = this.#router.currentRoute.value;
    // Forced: the same address again, so the guard runs.
    await this.#router.replace({ path, query, hash, force: true });
  }
}
